import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import diacritic from 'diacritic';

import { FormControlChildAbstract } from '~/component/form/abstracts/control-child.abstract';
import { InputStyleEnum } from '~/component/form/input/enums/style.enum';
import { propertyHasBeenEmptied } from '~/core/helpers/lifecycle-hooks/on-changes/property-has-been-emptied.helper';
import { generateUid } from '~/core/helpers/uid/uid.helper';

import { AutocompleteSuggestion } from './interfaces/suggestion.interface';
import { ListStyleEnum } from './list/enum/list-style.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';

@Component({
  selector: 'app-form-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FormAutocompleteComponent,
      multi: true,
    },
  ],
})
export class FormAutocompleteComponent
  extends FormControlChildAbstract<string>
  implements OnInit, OnChanges
{
  @Input() public ariaLabel: string;
  @Input() public autoFilter = false;
  @Input() public inputIcon: VtmnPlayIconTypeEnum;
  @Input() public inputStyle: InputStyleEnum;
  @Input() public isClearable = false;
  @Input() public placeholder = '';
  @Input() public suggestions: AutocompleteSuggestion[] = [];
  @Input() public triggerText = '';
  @Output() public readonly suggestion = new EventEmitter<string>();

  private readonly listStyleEnum = ListStyleEnum;

  public readonly id = generateUid();

  private selectedSuggestion: AutocompleteSuggestion;

  public isOpen = false;
  public suggestionsDisplayed: AutocompleteSuggestion[] = [];
  public triggerControl: FormControl;

  constructor(
    private readonly autocompleteComponent: ElementRef,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (propertyHasBeenEmptied(changes, 'triggerText')) {
      this.clearInputTrigger();
    }
  }

  public ngOnInit(): void {
    this.selectedSuggestion = {
      text: this.triggerText,
      selected: this.value !== '',
      value: this.value,
    };

    this.suggestionsDisplayed = this.suggestions;

    this.buildForm();
  }

  public get listStyle(): ListStyleEnum {
    return (
      [InputStyleEnum.Shadowed, InputStyleEnum.Transparent].includes(
        this.inputStyle
      ) && this.listStyleEnum.Shadowed
    );
  }

  @HostListener('document:click', ['$event.target'])
  private clickout(target): void {
    if (
      !this.autocompleteComponent.nativeElement.contains(target) &&
      this.isOpen
    ) {
      this.hideSuggestions();
    }
  }

  private buildForm(): void {
    this.triggerControl = new FormControl(
      this.selectedSuggestion.text,
      Validators.required
    );
  }

  private filterSuggestions(triggerText: string): void {
    this.suggestionsDisplayed = this.suggestions.filter((suggestion) => {
      if (!suggestion.text || !triggerText) {
        return suggestion;
      }

      return diacritic
        .clean(suggestion.text)
        .toLowerCase()
        .includes(diacritic.clean(triggerText.toLowerCase()));
    });
  }

  public clearInputTrigger(): void {
    this.triggerControl.setValue(null);
    this.value = undefined;
    this.isOpen = false;
    this.suggestionsDisplayed = this.suggestions;
  }

  public focusInputTrigger(): void {
    this.showSuggestions();
  }

  public hideSuggestions(): void {
    this.triggerControl.setValue(this.selectedSuggestion.text);
    this.isOpen = false;
  }

  public setSelectedSuggestion(suggestion: AutocompleteSuggestion): void {
    this.suggestionsDisplayed.forEach((suggestionDisplayed) => {
      suggestionDisplayed.selected = false;
    });

    this.selectedSuggestion = suggestion;
    this.selectedSuggestion.selected = true;
    this.value = this.selectedSuggestion.value;

    this.triggerControl.setValue(this.selectedSuggestion.text);

    setTimeout(() => {
      this.hideSuggestions();
    });
  }

  public setSuggestion(triggerText: string): void {
    if (!triggerText || this.selectedSuggestion.text === triggerText) {
      return;
    }

    this.suggestion.emit(triggerText);
    this.showSuggestions();

    if (this.autoFilter) {
      return this.filterSuggestions(triggerText);
    }

    return this.filterSuggestions(null);
  }

  public showSuggestions(): void {
    this.isOpen = true;
  }
}
